import { useMemo } from 'react';
import { AlertTriangle, ArrowRight, CalendarClock, CheckCircle2, Clock, Sparkles, TrendingDown, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useI18n, useTranslation } from '@solara/i18n';
import {
  BarChart,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  Sparkline,
  cn,
} from '@solara/ui-shared';
import { useInvoices, useInvoiceStats, useRecoveryFindings, useTddSubmissions } from '@solara/api-client/react';
import { adaptInvoices } from '../data/adapters';
import { LoadingBlock, ErrorBanner } from '../components/QueryState';
import { ComplianceSummary } from '../components/ComplianceSummary';

export default function HomePage() {
  const { t: tCommon } = useTranslation('common');
  const { t: tDash } = useTranslation('dashboard');
  const { t: tStatus } = useTranslation('common');
  const { t: tTs } = useTranslation('tickstar');
  const { locale } = useI18n();
  const aedFmt = new Intl.NumberFormat(locale, { style: 'currency', currency: 'AED' });
  const numberFmt = new Intl.NumberFormat(locale);
  const dateFmt = new Intl.DateTimeFormat(locale, { dateStyle: 'medium' });
  const pctFmt = new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 2 });

  const invoicesQuery = useInvoices();
  const statsQuery = useInvoiceStats();
  const tddQuery = useTddSubmissions();
  const recoveryQuery = useRecoveryFindings({ status: 'open' });

  const invoices = useMemo(
    () => adaptInvoices(invoicesQuery.data),
    [invoicesQuery.data],
  );

  const stats = useMemo(() => {
    const counts = { AP: 0, AB: 0, RE: 0, PENDING: 0 };
    let vat = 0;
    for (const inv of invoices) {
      counts[inv.mlsStatus]++;
      if (inv.mlsStatus === 'AP') vat += inv.totalVatAmount;
    }
    return { counts, vat, total: invoices.length };
  }, [invoices]);

  /** Rejection-rate trend over the last 30 days (1 bucket / 3 days). */
  const trend = useMemo(() => {
    const buckets = Array.from({ length: 10 }, () => ({ total: 0, rejected: 0 }));
    const now = new Date();
    for (const inv of invoices) {
      const ageDays = Math.floor((now.getTime() - new Date(inv.issueDate).getTime()) / 86400_000);
      const idx = Math.floor(ageDays / 3);
      if (idx < 0 || idx >= buckets.length) continue;
      const bucket = buckets[buckets.length - 1 - idx]!;
      bucket.total++;
      if (inv.mlsStatus === 'RE') bucket.rejected++;
    }
    const series = buckets.map((b) => (b.total === 0 ? 0 : b.rejected / b.total));
    const current = series[series.length - 1] ?? 0;
    return { series, current };
  }, [invoices]);

  /** Top 5 buyers by gross spend (uses full buyer name). */
  const spendByCategory = useMemo(() => {
    const map = new Map<string, number>();
    for (const inv of invoices) {
      const key = inv.buyerName.trim() || 'Unknown buyer';
      map.set(key, (map.get(key) ?? 0) + inv.totalGrossAmount);
    }
    return Array.from(map.entries())
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }, [invoices]);

  const recent = useMemo(() => invoices.slice(0, 5), [invoices]);

  /** Top 3 TDD submissions due soonest, excluding already-accepted ones. */
  const upcomingTdd = useMemo(() => {
    const now = Date.now();
    const tddList = tddQuery.data ?? [];
    return tddList
      .filter((td) => td.status !== 'ACCEPTED')
      .map((td) => {
        const inv = invoices.find((i) => i.invoiceId === td.invoiceId);
        const dueMs = new Date(td.deadlineAt).getTime() - now;
        return { td, inv, dueMs };
      })
      .filter((row) => row.inv != null)
      .sort((a, b) => a.dueMs - b.dueMs)
      .slice(0, 3);
  }, [tddQuery.data, invoices]);

  const recoveryTotals = useMemo(() => {
    // Backend `/analytics/recovery` ignores `?status=open` and returns the
    // whole feed; filter on the client. Statuses are UPPERCASE from Prisma.
    const all = recoveryQuery.data ?? [];
    const open = all.filter((f) => {
      const s = (f.status ?? '').toString().toUpperCase();
      return s === 'OPEN' || s === 'IN_REVIEW';
    });
    const pipelineAed = open.reduce((s, f) => {
      const raw = f as typeof f & { amountAedMinor?: string };
      const minor = raw.amountAedMinor != null ? Number(raw.amountAedMinor) : Number(f.amountAed) * 100;
      return s + (Number.isFinite(minor) ? minor / 100 : 0);
    }, 0);
    return { totalOpen: open.length, pipelineAed };
  }, [recoveryQuery.data]);

  // Use backend status counts when available — falls back to client-side
  // aggregation on the rendered invoice list.
  const statusCountsFromBackend = useMemo(() => {
    if (!statsQuery.data) return null;
    const map = { AP: 0, AB: 0, RE: 0, PENDING: 0 } as Record<string, number>;
    for (const row of statsQuery.data) {
      const key =
        row.status === 'ACKNOWLEDGED' || row.status === 'ARCHIVED'
          ? 'AP'
          : row.status === 'DELIVERED' || row.status === 'SENDING' || row.status === 'SIGNED'
          ? 'AB'
          : row.status === 'REJECTED' || row.status === 'FAILED'
          ? 'RE'
          : 'PENDING';
      map[key] = (map[key] ?? 0) + row._count._all;
    }
    return map;
  }, [statsQuery.data]);

  const counts = statusCountsFromBackend ?? stats.counts;

  if (invoicesQuery.isLoading) return <LoadingBlock />;
  if (invoicesQuery.error) return <ErrorBanner error={invoicesQuery.error} />;

  return (
    <div className="space-y-6">
      {/* P1: compliance snapshot strip — KYC progress, tasks, tax deadlines, approvals. */}
      <ComplianceSummary />

      {/* Hero */}
      <section
        className={cn(
          'rounded-2xl p-6 sm:p-8 lg:p-10',
          'bg-gradient-to-br from-brand-500 to-brand-700 text-white',
          'shadow-lg overflow-hidden relative',
        )}
      >
        <Sparkles className="hidden sm:block h-32 w-32 absolute -end-6 -top-6 opacity-15" />
        <div className="relative max-w-2xl">
          <p className="text-sm/none uppercase tracking-widest opacity-80">
            {tCommon('platform.name')}
          </p>
          <h1 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold leading-tight">
            {tCommon('welcome.portal.title')}
          </h1>
          <p className="mt-3 text-sm sm:text-base text-white/85">
            {tCommon('welcome.portal.subtitle')}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button asChild variant="secondary" size="lg" className="bg-white text-brand-700 border-transparent hover:bg-white/95">
              <Link to="/onboarding" className="gap-2">
                {tCommon('welcome.portal.cta')}
                <ArrowRight className="h-4 w-4 rtl-mirror" />
              </Link>
            </Button>
            <Button asChild variant="ghost" size="lg" className="text-white hover:bg-white/15">
              <Link to="/new">{tCommon('nav.newInvoice')}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Status overview */}
      <section className="grid gap-3 sm:gap-4 grid-cols-2 lg:grid-cols-4">
        <StatusCard
          tone="success"
          icon={<CheckCircle2 className="h-5 w-5" />}
          label={tStatus('status.ap')}
          value={numberFmt.format(counts.AP)}
        />
        <StatusCard
          tone="info"
          icon={<Clock className="h-5 w-5" />}
          label={tStatus('status.ab')}
          value={numberFmt.format(counts.AB)}
        />
        <StatusCard
          tone="danger"
          icon={<XCircle className="h-5 w-5" />}
          label={tStatus('status.re')}
          value={numberFmt.format(counts.RE)}
        />
        <Card className="col-span-2 lg:col-span-1 rounded-2xl">
          <CardBody className="p-4 sm:p-5 flex flex-col gap-2">
            <span className="text-xs uppercase tracking-wide text-fg-subtle">{tDash('stats.vatCollected')}</span>
            <span className="text-2xl font-semibold num">{aedFmt.format(stats.vat)}</span>
            <span className="text-xs text-fg-subtle">
              {tDash('stats.vatCollectedHint', { count: counts.AP })}
            </span>
          </CardBody>
        </Card>
      </section>

      {/* Trend + spend chart */}
      <section className="grid gap-6 grid-cols-1 lg:grid-cols-3">
        <Card className="rounded-2xl lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <CardTitle>{tDash('trend.title')}</CardTitle>
                <p className="text-xs text-fg-subtle mt-0.5">{tDash('trend.subtitle')}</p>
              </div>
              <Badge tone={trend.current < 0.03 ? 'success' : trend.current < 0.06 ? 'warning' : 'danger'}>
                {tDash('trend.current')} {pctFmt.format(trend.current)}
              </Badge>
            </div>
          </CardHeader>
          <CardBody>
            <div className="flex items-center gap-4">
              <TrendingDown className={cn('h-8 w-8 shrink-0', trend.current < 0.03 ? 'text-success' : 'text-warning')} />
              <Sparkline
                values={trend.series}
                width={400}
                height={64}
                stroke="rgb(var(--color-brand-500))"
                fill="rgb(var(--color-brand-100) / 0.6)"
                className="flex-1 max-w-full"
              />
            </div>
          </CardBody>
        </Card>

        <Card className="rounded-2xl">
          <CardHeader>
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div>
                <CardTitle>{tDash('spend.title')}</CardTitle>
                <p className="text-xs text-fg-subtle mt-0.5">{tDash('spend.subtitle')}</p>
              </div>
              <span className="inline-flex items-center rounded-full bg-amber-50 text-amber-700 border border-amber-100 px-2 py-0.5 text-[10px] font-medium" title={tTs('brand.partnerLong')}>
                {tTs('modules.smartInbox.aiBadge')}
              </span>
            </div>
          </CardHeader>
          <CardBody>
            {spendByCategory.length === 0 ? (
              <p className="text-sm text-fg-muted">{tDash('spend.empty')}</p>
            ) : (
              <BarChart
                data={spendByCategory}
                format={(n) => aedFmt.format(n)}
              />
            )}
            <div className="mt-4 flex justify-end">
              <Button asChild variant="ghost" size="sm" className="text-fg-muted">
                <Link to="/spend" className="gap-1">
                  {tCommon('nav.spend')}
                  <ArrowRight className="h-4 w-4 rtl-mirror" />
                </Link>
              </Button>
            </div>
          </CardBody>
        </Card>
      </section>

      {/* Recovery audit teaser */}
      {recoveryTotals.totalOpen > 0 ? (
        <section>
          <Card className="rounded-2xl border-amber-100 bg-amber-50/40">
            <CardBody className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <div className="h-10 w-10 rounded-xl bg-amber-100 text-amber-700 grid place-items-center shrink-0">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-sm flex items-center gap-2 flex-wrap">
                    <span>{tTs('modules.recoveryAudit.title')}</span>
                    <Badge tone="warning">
                      {tTs('modules.recoveryAudit.kpi.open')}: {numberFmt.format(recoveryTotals.totalOpen)}
                    </Badge>
                  </div>
                  <p className="text-xs text-fg-muted mt-1">
                    {tTs('modules.recoveryAudit.kpi.pipeline')}: <span className="num font-medium text-fg">{aedFmt.format(recoveryTotals.pipelineAed)}</span>
                  </p>
                </div>
              </div>
              <Button asChild variant="outline" size="sm" className="border-amber-300 text-amber-700 hover:bg-amber-100 shrink-0">
                <Link to="/recovery-audit" className="gap-1">
                  {tCommon('nav.recoveryAudit')}
                  <ArrowRight className="h-4 w-4 rtl-mirror" />
                </Link>
              </Button>
            </CardBody>
          </Card>
        </section>
      ) : null}

      {/* Upcoming TDD deadlines */}
      <section>
        <Card className="rounded-2xl">
          <CardHeader>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <CardTitle>{tDash('upcoming.title')}</CardTitle>
                <p className="text-xs text-fg-subtle mt-0.5">{tDash('upcoming.subtitle')}</p>
              </div>
              <Button asChild variant="ghost" size="sm" className="text-fg-muted">
                <Link to="/compliance" className="gap-1">
                  {tDash('upcoming.viewAll')}
                  <ArrowRight className="h-4 w-4 rtl-mirror" />
                </Link>
              </Button>
            </div>
          </CardHeader>
          <CardBody>
            {upcomingTdd.length === 0 ? (
              <EmptyState
                icon={<CalendarClock className="h-6 w-6" />}
                title={tDash('upcoming.empty')}
              />
            ) : (
              <ul className="grid gap-3 sm:grid-cols-3">
                {upcomingTdd.map(({ td, inv, dueMs }) => {
                  const overdue = dueMs < 0;
                  const days = Math.max(0, Math.floor(dueMs / 86_400_000));
                  // Overdue display: switch from hours to days once we're past 24h.
                  const overdueMs = -dueMs;
                  const overdueDays = Math.floor(overdueMs / 86_400_000);
                  const overdueHours = Math.max(0, Math.floor(overdueMs / 3_600_000));
                  return (
                    <li key={td.id}>
                      <Link
                        to={`/invoices/${inv!.invoiceId}`}
                        className={cn(
                          'block rounded-xl border p-3 transition-colors hover:bg-surface-muted',
                          overdue ? 'border-danger/40 bg-danger/5' : days <= 2 ? 'border-warning/40 bg-warning/5' : 'border-border',
                        )}
                      >
                        <div className="flex items-center gap-2">
                          {overdue ? (
                            <AlertTriangle className="h-4 w-4 text-danger" />
                          ) : (
                            <CalendarClock className="h-4 w-4 text-warning" />
                          )}
                          <span className="text-sm font-medium truncate">{inv!.invoiceNumber}</span>
                        </div>
                        <div className="text-xs text-fg-subtle mt-1 truncate" dir="auto">
                          {locale === 'ar-AE' ? inv!.buyerNameAr : inv!.buyerName}
                        </div>
                        <div className="text-xs mt-1.5">
                          <span className="num font-semibold">{aedFmt.format(inv!.totalVatAmount)}</span>
                          <span className="text-fg-subtle"> · </span>
                          {overdue ? (
                            <span className="text-danger font-medium">
                              {overdueDays >= 1
                                ? tDash('upcoming.overdueByDays', { count: overdueDays })
                                : tDash('upcoming.overdueByHours', { count: overdueHours })}
                            </span>
                          ) : (
                            <span className={days <= 2 ? 'text-warning font-medium' : 'text-fg-muted'}>
                              {tDash('upcoming.dueIn', { count: days })}
                            </span>
                          )}
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardBody>
        </Card>
      </section>

      {/* Recent activity */}
      <section className="grid gap-6 grid-cols-1 lg:grid-cols-3">
        <Card className="lg:col-span-2 rounded-2xl">
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <CardTitle>{tDash('recent.title')}</CardTitle>
              <Button asChild variant="ghost" size="sm" className="text-fg-muted">
                <Link to="/invoices" className="gap-1">
                  {tDash('recent.seeAll')}
                  <ArrowRight className="h-4 w-4 rtl-mirror" />
                </Link>
              </Button>
            </div>
          </CardHeader>
          <CardBody className="space-y-2">
            {recent.map((inv) => {
              const buyer = locale === 'ar-AE' ? inv.buyerNameAr : inv.buyerName;
              const tone =
                inv.mlsStatus === 'AP'
                  ? 'success'
                  : inv.mlsStatus === 'AB'
                  ? 'info'
                  : inv.mlsStatus === 'RE'
                  ? 'danger'
                  : 'neutral';
              return (
                <Link
                  key={inv.invoiceId}
                  to={`/invoices/${inv.invoiceId}`}
                  className="flex items-center justify-between gap-3 rounded-xl border border-border px-3 py-2.5 hover:bg-surface-muted transition-colors"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{inv.invoiceNumber}</span>
                      <Badge tone={tone}>{inv.mlsStatus}</Badge>
                    </div>
                    <div className="text-xs text-fg-subtle truncate" dir="auto">
                      {buyer} · {dateFmt.format(new Date(inv.issueDate))}
                    </div>
                  </div>
                  <span className="text-sm font-semibold num shrink-0">
                    {aedFmt.format(inv.totalGrossAmount)}
                  </span>
                </Link>
              );
            })}
          </CardBody>
        </Card>

        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle>{tDash('compliance.title')}</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3 text-sm">
            <Row label={tDash('compliance.emaratax')} value={<Badge tone="success">{tDash('compliance.connected')}</Badge>} />
            <Row label={tDash('compliance.mfa')} value={<Badge tone="success">{tDash('compliance.enabled')}</Badge>} />
            <Row label={tDash('compliance.erp')} value={<Badge tone="warning">{tDash('compliance.notConfigured')}</Badge>} />
            <Row
              label={tDash('compliance.nextTdd')}
              value={
                <span className="text-fg-muted">
                  {dateFmt.format(new Date(Date.now() + 5 * 86400000))}
                </span>
              }
            />
          </CardBody>
        </Card>
      </section>
    </div>
  );
}

function StatusCard({
  tone,
  icon,
  label,
  value,
}: {
  tone: 'success' | 'danger' | 'info';
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  const accent: Record<typeof tone, string> = {
    success: 'text-success bg-emerald-50',
    danger: 'text-danger bg-red-50',
    info: 'text-info bg-cyan-50',
  };
  return (
    <Card className="rounded-2xl">
      <CardBody className="p-4 sm:p-5 flex items-center gap-3">
        <div className={cn('h-10 w-10 grid place-items-center rounded-xl shrink-0', accent[tone])}>
          {icon}
        </div>
        <div className="flex flex-col leading-tight min-w-0">
          <span className="text-xs uppercase tracking-wide text-fg-subtle truncate">{label}</span>
          <span className="text-xl font-semibold num">{value}</span>
        </div>
      </CardBody>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-fg-muted">{label}</span>
      <span>{value}</span>
    </div>
  );
}
